import { Map, List, fromJS } from "immutable";
import IDUtils from "./IDUtils";
import BlockUpdateVO from "../vo/BlockUpdate.mjs";
import { ensureArray } from "./JSUtils";
import DocumentStore from "../document/DocumentStore";
import { COMMANDS } from "../document/commands";
import { batchPropChanges } from "../components/utils/PropsUpdater";
import { ColorParts } from "./ColorUtils";
import AppConstants from "../constants/AppConstants";
import { prepElementData } from "./InheritanceUtils";
import { deviatePropFromUpdate, applyStyle } from "./InheritanceUtils";
import {
  gatherProps,
  shouldSyncInherit,
  shouldPropInherit,
  isInheritedPropSynced,
} from "./Merge/MergeUtils.mjs";
import { isSynced } from "./SyncUtils";
import {
  LAYOUT_DESKTOP,
  LAYOUT_MOBILE,
  MIXED,
  MIXED_SYNC,
  SYNCED_PROPS,
} from "../constants/DesignConstants.mjs";
import {
  redirectUpdate,
  getPropUpdateDetails,
  updateIsAlphaChange,
  getUpdateType,
  deleteProp,
  updateShouldForceMerge,
} from "./PropUpdateUtils";
import { updateIsDeviation } from "./PropUpdateUtils";
import { PROCESSES, MERGE, SPLIT, STYLE } from "../constants/PropUpdateConstants.mjs";

/**
 * @typedef {import("../vo/BlockUpdate.mjs").PropUpdate} PropUpdate
 */

const { DEFAULT_OPACITY, UPDATE_ACTION_SET } = AppConstants;

const IGNORE_KEYS = ["visible", "selected", "name", "slug"];

/**
 * Filters our top level keys not necessary for panel usage.
 * @param {Map} element
 * @returns {Map}
 */
export const removeIgnoreKeys = (element) => element.filter((_, key) => !IGNORE_KEYS.includes(key));

/**
 * Checks value for theme/hex/mixed color format
 * @param {*} value
 * @returns {boolean}
 */
export const valueIsColor = (value) =>
  typeof value === "string" &&
  (value.startsWith("colors") || value.startsWith("#") || value.startsWith(`${MIXED}:`));

// todo: move to ColorUtils maybe? Not sure where it is more relevant.
/**
 * Checks color and alpha values. Mixes them where appropriate.
 * @param {string=} value1
 * @param {string=} value2
 * @returns {string}
 */
export const mixColor = (value1, value2) => {
  const color1 = ColorParts.fromString(value1);
  const color2 = ColorParts.fromString(value2);

  const colorMatch = color1.color === color2.color;
  const alphaMatch = color1.alpha === color2.alpha;
  const useAlpha = !(alphaMatch && color1.alpha === DEFAULT_OPACITY);

  return `${colorMatch ? color1.color : MIXED}${
    useAlpha ? `:${alphaMatch ? color1.alpha : MIXED}` : ""
  }`;
};

/**
 * Gets element data from the block using element id. The element object passed in just has id and some irrelevant props. Not the element itself.
 * @param {Object} elements
 * @param {Map} block
 * @returns {Map}
 */
export const getElements = (elements, block) =>
  elements.map((element) => block.getIn(IDUtils.getArray(element.id, 1)));

/**
 * Recursively condenses mixed values in a map, or preserves them if they are the same.
 * @param {Map} element1
 * @param {Map} element2
 * @param {string=} carry
 * @returns {Map}
 */
export const mixElements = (element1 = fromJS({}), element2 = fromJS({}), carry = []) =>
  Array.from(new Set([...element1.keys(), ...element2.keys()])).reduce((result, key) => {
    const composedKey = [...carry, key];
    const value1 = element1.get(key);
    const value2 = element2.get(key);

    return result.setIn(
      [key],
      value1 === MIXED || value2 === MIXED
        ? MIXED
        : Map.isMap(value1) && Map.isMap(value2)
        ? mixElements(value1, value2, composedKey)
        : (Map.isMap(value1) && !value2) || (Map.isMap(value2) && !value1)
        ? MIXED
        : List.isList(value1) || List.isList(value2)
        ? (value1 ?? List([])).equals(value2)
          ? value1
          : MIXED
        : valueIsColor(value1) || valueIsColor(value2)
        ? mixColor(value1, value2)
        : value1 === value2
        ? value1
        : MIXED
    );
  }, Map());

/**
 * Combines elements, replaces mixed values with "mixed", and replaces sync array with entries that
 * match, and adds a mixedSync array where differing sync values are found
 * @param {Map} elements
 * @param {Map} block
 */
export const conjoinElements = (elements, block, theme) => {
  const elementData = getElements(elements, block).map((elementData) =>
    prepElementData(elementData, theme)
  );

  let mixedElement = removeIgnoreKeys(elementData.first());
  let sharedSync = new Set(mixedElement.get(SYNCED_PROPS, List()).toJS());
  let mixedSync = new Set();

  elementData.rest().forEach((element) => {
    const currentSync = new Set(element.get(SYNCED_PROPS, List()).toJS());
    mixedSync = sharedSync.symmetricDifference(currentSync);
    sharedSync = sharedSync.intersection(currentSync);

    const filteredElement = removeIgnoreKeys(element);
    mixedElement = mixElements(mixedElement, filteredElement);
  });

  return mixedElement
    .set(SYNCED_PROPS, List(Array.from(sharedSync)))
    .set(MIXED_SYNC, List(Array.from(mixedSync)));
};

/**
 * Checks that all elements have the same type
 * @param {Map} elements
 * @param {Map} block
 * @returns {boolean}
 */
export const typesMatch = (elements, block) =>
  new Set(
    getElements(elements, block)
      .valueSeq()
      .map((element) => element.get("type"))
  ).size === 1;

/**
 * Change update layout if is a style dependent prop
 * @param {*} propUpdate
 * @param {*} elementData
 * @param {*} block
 * @param {*} theme
 * @returns
 */
export const redirectUpdates = (propUpdate, elementData, block, theme) => {
  const [propName, updateLayout] = getPropUpdateDetails(propUpdate);
  const inheritsProp = shouldPropInherit(elementData, updateLayout, propName, theme);
  const inheritsSync = shouldSyncInherit(elementData, propName, theme);
  const synced = isSynced(elementData, propName, updateLayout, theme);

  const shouldRedirect =
    inheritsProp && inheritsSync
      ? isInheritedPropSynced(elementData, updateLayout, propName, theme)
      : synced;

  return shouldRedirect
    ? redirectUpdate(propUpdate, LAYOUT_DESKTOP)
    : {
        ...propUpdate,
      };
};

/**
 * Replaces sync array for incoming merge/split sets of updates.
 * Returns a new set of updates for immutability between element processes.
 * @param {Array<PropUpdate>} propUpdates
 * @param {Map} elementData
 * @param {PROCESSES[number]} type
 */
export const replaceSync = (propUpdates, elementData, type) => {
  const syncUpdateIndex = propUpdates.findIndex(({ name }) => name === SYNCED_PROPS);
  const mobileUpdates = propUpdates.filter(({ name }) => name?.includes(LAYOUT_MOBILE));
  const currentSync = elementData.get(SYNCED_PROPS);
  const newSync = mobileUpdates.reduce((result, mobileUpdate) => {
    const [propName] = getPropUpdateDetails(mobileUpdate);
  
    type === MERGE
      ? result.add(propName)
      : type === SPLIT
      ? result.delete(propName)
      : undefined;

    return result;
  }, new Set(currentSync));

  const newUpdates = [...propUpdates];

  const syncUpdate = newUpdates[syncUpdateIndex];
  if (syncUpdate) {
    newUpdates[syncUpdateIndex] = newSync.size
      ? { ...syncUpdate, value: List(newSync), action: UPDATE_ACTION_SET }
      : { ...syncUpdate, ...deleteProp(SYNCED_PROPS, syncUpdate.addUndo) };
  }

  return newUpdates;
};

/**
 * Generates a sync-like update if merge should be forced.
 * @param {Array<PropUpdate>} propUpdates
 * @param {Map} elementData
 * @returns {Array<PropUpdate>}
 */
const forceSync = (propUpdates, elementData) => {
  const baseUpdate = ensureArray(propUpdates)[0];
  const [propName] = getPropUpdateDetails(baseUpdate);

  if (!isSynced(elementData, propName, LAYOUT_DESKTOP)) {
    const mobileUpdate = deleteProp(`mobile.${propName}`);
    const syncUpdate = {
      name: SYNCED_PROPS,
      value: List(new Set([...elementData.get(SYNCED_PROPS, List()), propName])),
      action: UPDATE_ACTION_SET,
    };

    return [baseUpdate, mobileUpdate, syncUpdate];
  }

  return ensureArray(propUpdates);
};

/**
 * Grabs the element's previously set color and applies alpha to it.
 * @param {PropUpdate} propUpdate
 * @param {Map} elementData
 * @param {Map} block
 * @returns {PropUpdate}
 */
const multicastAlpha = (propUpdate, elementData, theme) => {
  if (updateIsAlphaChange(propUpdate)) {
    const [propName, updateLayout] = getPropUpdateDetails(propUpdate);
    const propPath = propName.split(".");

    const props = gatherProps(elementData, updateLayout, propPath, undefined, theme);
    const prevColorValue = props.getIn(propPath);

    const prevColor = ColorParts.fromString(prevColorValue);
    const colorUpdate = ColorParts.fromString(propUpdate?.value);

    propUpdate.value = ColorParts.toString({ ...prevColor, alpha: colorUpdate.alpha });
  }

  return propUpdate;
};

/**
 * Multicasts the deviation process if update is detected to be a deviation.
 * @param {PropUpdate} propUpdate
 * @param {Map} elementData
 * @param {Map} block
 * @param {Map} theme
 * @returns {Array<PropUpdate>}
 */
const multicastDeviation = (propUpdate, elementData, theme) =>
  updateIsDeviation(elementData, propUpdate, theme)
    ? deviatePropFromUpdate(elementData, propUpdate, theme)
    : ensureArray(propUpdate);

const MULTICAST_STRATEGIES = [redirectUpdates, multicastAlpha, multicastDeviation];

/**
 * Runs a propUpdate through all multicast strategies
 * @param {PropUpdate} propUpdate
 * @param {Map} element
 * @param {Map} block
 * @param {Map} theme
 * @returns {Array<PropUpdate>}
 */
const prepStrategies = (strategies) => (propUpdate, element, theme) =>
  strategies.reduce((result, strategy) => strategy(result, element, theme), {
    ...propUpdate,
  });

/**
 * Prepares a callback that will take a propInfo (or list of them) and apply them generate relevant updates for each element.
 * @param {Object} props
 * @param {Map} props.elementInfos -
 * @param {Map=} props.preparedElementData
 * @param {typeof import("../constants/DesignConstants.mjs").LAYOUTS[number]} props.layout
 * @param {Map} props.block
 * @param {Map} props.theme
 * @returns
 */
export const prepareMulticastUpdate =
  ({ elementInfos, preparedElementData, layout, block, theme }) =>
  (/** @type {PropUpdate | Array<PropUpdate>} */ propInfo) => {
    const update = new BlockUpdateVO();

    // generate a set of propInfo updates for each element
    elementInfos.forEach(({ id: elementId }) => {
      const elementData = block.getIn(IDUtils.getArray(elementId, 1));

      const propInfos = [
        ...(updateShouldForceMerge(propInfo, preparedElementData)
          ? forceSync(propInfo, elementData)
          : ensureArray(propInfo)),
      ];

      const updateType = getUpdateType({
        elementData,
        preparedElementData,
        propInfo: propInfos,
        theme,
      });

      const preparedUpdates = [];
      // todo: convert this to a builder if it gets any more complex
      if (updateType === STYLE) {
        preparedUpdates.push(...applyStyle(elementData, propInfos, layout));
      } else if (updateType === MERGE || updateType === SPLIT) {
        // effectively the same process with some sync differences
        const preppedSyncUpdates = replaceSync(propInfos, elementData, updateType);
        const runStrategies = prepStrategies([multicastAlpha]);

        preparedUpdates.push(
          ...preppedSyncUpdates.map((propInfo) => runStrategies(propInfo, elementData, theme))
        );
      } else {
        const runStrategies = prepStrategies(MULTICAST_STRATEGIES);
        preparedUpdates.push(
          ...propInfos.map((propInfo) => runStrategies(propInfo, elementData, theme))
        );
      }

      // apply current element id to all updates during add
      const updaterCB = (propUpdate) => {
        ensureArray(propUpdate).forEach((singleUpdate) =>
          update.addPropUpdate(elementId, singleUpdate)
        );
      };

      batchPropChanges(preparedUpdates, updaterCB);
    });

    update.collapseUndos();

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  };
